import { ScopeType, SortState, VariableEdit } from './types';
import _ from 'lodash';
import { VariableInput } from 'src/graphql/_generated';
import * as Yup from 'yup';

export const genEmptyVariable = (scope: ScopeType): VariableEdit => {
  return {
    id: Date.now(),
    name: '',
    alias: null,
    // @ts-ignore
    value: undefined,
    currency: false,
    workspace_variable: scope === 'workspace',
    isInUse: [],
    isGlobal: false,
    _isNew: true,
  };
};

const isSameVariable = (a: VariableEdit, b: VariableEdit) =>
  a.id === b.id && a.workspace_variable === b.workspace_variable && a.isGlobal === b.isGlobal;

export const findChangedVariables = (original: VariableEdit[], modified: VariableEdit[]) => {
  return modified.filter((item) => {
    const originalItem = original.find((o) => isSameVariable(o, item));
    if (!originalItem || item._isNew) {
      return false;
    }

    return (
      item.name !== originalItem.name || item.value !== originalItem.value || item.currency !== originalItem.currency
    );
  });
};

export const categorizeVariables = (
  input: VariableEdit[],
  original: VariableEdit[],
  excludeDeleteNames: string[],
  label?: string,
) => {
  const newVariables = input
    .filter((el) => el._isNew || el._isNewOverwrite)
    .map((el) => {
      if (el._isNewOverwrite) return _.omit(el, ['_isNew', '_isNewOverwrite']);
      return _.omit(el, ['_isNew', '_isNewOverwrite', 'id']);
    });

  const changedVariables = findChangedVariables(original, input).map((el) => {
    if (el.isGlobal) return { ...el, value: _.isNil(el.value) ? null : el.value };
    return el;
  });

  const deletedVariables = original
    .filter((el) => !input.find((v) => isSameVariable(v, el)))
    .map((el) => {
      return { ...el, value: null };
    })
    .filter((el) => !excludeDeleteNames.includes(el.name));

  return [...newVariables.map((el) => ({ ...el, id: undefined })), ...changedVariables, ...deletedVariables];
};

const uniqueNameTest = {
  name: 'unique-name',
  // @ts-ignore
  test: function () {
    // @ts-ignore
    const { path, from } = this;

    const existingValue = _.get(from[1].value, path)?.toLowerCase()?.trim();
    const values = from[1].value.variables.map((el: any) => el.name?.toLowerCase()?.trim());
    const duplicate = _.filter(values, (v) => v === existingValue).length > 1;

    if (duplicate) {
      const errorMessage = 'Variable name must be unique.';

      // @ts-ignore
      return this.createError({
        path,
        message: errorMessage,
      });
    }
    return true;
  },
  message: 'Name must be unique',
};

const MAX_VARIABLES = 50;

export const validationSchema = Yup.object({
  variables: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number().nullable(),
        name: Yup.string()
          .min(1, 'Name is too short')
          .required('Required')
          .test(uniqueNameTest)
          .matches(/^[a-zA-Z0-9-_()<>|:@?!\s]+$/, 'Contains unsupported characters'),
        value: Yup.number()
          .nullable()
          .when(['isGlobal'], (values, schema) => {
            if (values[0] === true) {
              return Yup.number().nullable().notRequired();
            } else {
              return Yup.number().nullable().min(0, 'Value must be equal to or greater than 0').required('Required');
            }
          }),

        currency: Yup.boolean().required('Required'),
        isInUse: Yup.array().of(Yup.string()).nullable(),
        workspace_variable: Yup.boolean().required('Required'),
      }),
    )
    .nullable()
    .max(MAX_VARIABLES, `You can add up to ${MAX_VARIABLES} variables`),
});

export const checkIsDirty = (arr1: VariableEdit[], arr2: VariableEdit[]) => {
  const sortedArr1 = _.sortBy(arr1, 'id');
  const sortedArr2 = _.sortBy(arr2, 'id');

  const areEqual = _.isEqual(sortedArr1, sortedArr2);

  return !areEqual;
};

export const sortVariables = (variables: VariableEdit[], sort: SortState) => {
  const [newVariables, existingVariables] = _.partition(variables, (el) => el._isNew);
  const sortedExisting = _.orderBy(
    existingVariables,
    [
      (el) => {
        const value = el[sort.by];
        return typeof value === 'string' ? value.toLowerCase() : value;
      },
    ],
    [sort.isAsc ? 'asc' : 'desc'],
  );
  const sorted = [...sortedExisting, ...newVariables];
  if (!_.isEqual(sorted, variables)) {
    return sorted;
  } else {
    return undefined;
  }
};

export const generateUsageMessage = (data: {
  workspace_variable: boolean;
  id: number;
  assessmentNames?: string[];
  scenarioNames?: string[];
  isGlobal: boolean;
}): string => {
  const usageType = data.workspace_variable ? 'assessments' : 'scenarios';
  const names = (data.workspace_variable ? data.assessmentNames : data.scenarioNames) || [];

  const messagePrefix = data.isGlobal
    ? 'Default variables cannot be deleted. This variable is in use in the following'
    : "This variable can't be deleted because it is in use in the following";

  return `${messagePrefix} ${usageType}:</br><strong>${(_.uniq(names) || []).join(', ')}</strong>`;
};

export const transformVariablesToGqlInput = (input: ReturnType<typeof categorizeVariables>): VariableInput[] => {
  return input.map((el) => ({
    currency: el.currency,
    isGlobal: el.isGlobal,
    name: el.name,
    value: el.value,
    id: el.id,
  }));
};
